import { FC, useMemo, useState } from 'react';

import Select, {
  ActionMeta,
  ControlProps,
  OptionProps,
  SingleValue,
  components,
} from 'react-select';
import { cn } from 'utils/helpers';

import { useGetEvent, useUpdateEventStatus } from '../hooks';
import { useEventContext } from '../utils/providers/EventContext';

const STATUS_OPTIONS: Record<string, any>[] = [
  { value: 'PENDING', label: 'Pending', color: '#4A545E' },
  { value: 'COMPLETED', label: 'Completed', color: '#23CC67' },
  { value: 'NO_SHOW', label: 'No Show', color: '#E22323' },
];

const Control = ({ children, ...props }: ControlProps<any>) => {
  // const selected = props.getValue();
  return (
    <components.Control
      {...props}
      className={cn(
        'flex items-center px-2',
        '!border-none',
        '!shadow-none !rounded-[6px] !min-h-[36px] h-full',
      )}
    >
      {children}
    </components.Control>
  );
};

const Option = (props: OptionProps<any>) => {
  const { data, isFocused, isSelected } = props;
  return (
    <components.Option
      {...props}
      className={cn(
        '!flex gap-[14px] !items-center',
        'hover:bg-fill-primary',
        (isSelected || isFocused) &&
          '!bg-fill-primary !text-secondary-dark font-semibold',
      )}
    >
      <div className="text-sm leading-5 tracking-wider">{`${data?.label}`}</div>
    </components.Option>
  );
};

const EventStatusButton: FC = () => {
  const { selectedEventId } = useEventContext();
  const { data: event, isLoading: isEventLoading } = useGetEvent(
    selectedEventId as string,
  );
  const [status, setStatus] = useState(event?.status ?? 'PENDING');
  const { mutate: updateEventStatus, isLoading: isEventStatusLoading } =
    useUpdateEventStatus();

  const defaultVal = useMemo(
    () => STATUS_OPTIONS.find((opt) => opt.value === status),
    [status],
  );

  const onChange: (
    newValue: SingleValue<any>,
    actionMeta: ActionMeta<any>,
  ) => void = ({ value }: Record<string, any>) => {
    setStatus(value);

    updateEventStatus({ id: event._id, values: { status: value } });
  };

  const disabled = useMemo(
    () => isEventLoading || isEventStatusLoading,
    [isEventLoading, isEventStatusLoading],
  );

  return (
    <div>
      <Select
        defaultValue={defaultVal}
        onChange={onChange}
        isDisabled={disabled}
        className={cn([
          'text-white w-full',
          '!shadow-none rounded-[6px]',
          'text-sm',
          'border-none border-separator-secondary',
          'disabled:border-fill-secondary disabled:text-disabled',
          'group-focus:border-primary-base group-focus-visible:border-primary-base',
          // '!focus:outline-none focus:border-none',
        ])}
        isSearchable={false}
        components={{
          Control,
          Option,
          IndicatorSeparator: null,
        }}
        styles={{
          singleValue: (provided) => ({
            ...provided,
            color: '#FFFFFF',
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            color: '#FFFFFF',
          }),
          control: (provided) => ({
            ...provided,
            backgroundColor: defaultVal?.color,
          }),
          valueContainer: (base) => ({ ...base, padding: '0 2px' }),
        }}
        options={STATUS_OPTIONS}
      />
    </div>
  );
};

export default EventStatusButton;
